import { ReactNode, useEffect, useMemo, useState } from "react";
import { Box, Grid, Tooltip, IconButton } from "@mui/material";
import ErrorBoundary from "../components/common/ErrorBoundary";
import MapComponent from "../components/map/MapComponent";
import Sidebar from "../components/common/SideBar";
import MediaDrawer from "../components/media/MediaDrawer";
import { useSelectionSelector } from "../store/selection";
import { useMediaDrawerRelationshipGate } from "../hooks/useSelectedEntity";
import EntityMapModal from "../components/modals/EntityMapModal";
import MediaDetailOverlay from "../components/modals/MediaDetailOverlay";
import { useUIActions, useUISelector } from "../store/UI";
import { useMediaSelector } from "../store/media";
import { usePopupSelector } from "../store/popupStore";
import { useLocation } from "react-router-dom";
import StartupModal from "../components/modals/startup-modal/StartupModal";
import { useSyncMapHighlights } from "../hooks/useSyncMapHighlights";
import CalendarView from "../components/calendar/CalendarView";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export const NAVBAR_HEIGHT = "4.1rem";
const CALENDAR_HEIGHT_PX = 200; // px — single source of truth
const CALENDAR_HEIGHT = `${CALENDAR_HEIGHT_PX}px`;

function Modals() {
  const { selectedEntityId } = useSelectionSelector();
  if (!selectedEntityId) return null;

  return (
    <>
      {/* Layer A: primary popup (geo/non-geo/media-viewer) */}
      <EntityMapModal />
      {/* Layer B (MediaDetailOverlay) is intentionally NOT rendered here — see
          MapContent below. It lives at the top-level stacking context so it
          can sit above the fullscreen MediaDrawer (`zIndex: 99`). Rendering it
          inside this container would trap its `zIndex: 1000` inside the
          surrounding `zIndex: 2` stacking context and the drawer would cover
          it. */}
    </>
  );
}

/**
 * MediaDetailOverlayHost — lifts MediaDetailOverlay (Layer B) out of
 * FlexibleContainer's stacking context so its `position: fixed; zIndex: 1000`
 * wrapper competes with MediaDrawer's fullscreen panel (zIndex: 99) in the
 * root stacking context. Gated on `selectedEntityId` to match the previous
 * behaviour of the inline `Modals` component (overlay only relevant when an
 * entity is selected). This gate can be relaxed when the future "browse all
 * media" feature lands.
 */
function MediaDetailOverlayHost() {
  const { selectedEntityId } = useSelectionSelector();
  if (!selectedEntityId) return null;
  return <MediaDetailOverlay />;
}

function FlexibleContainer({ children }: { children: ReactNode }) {
  const { isMediaDrawerVisible, mediaDrawerManuallyClosedByUser } = useUISelector();
  const mediaDrawerRelationshipGate = useMediaDrawerRelationshipGate();

  const isVisible = (isMediaDrawerVisible && mediaDrawerManuallyClosedByUser) || (isMediaDrawerVisible && !mediaDrawerManuallyClosedByUser);

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        pointerEvents: "none",
        height: "100%",
        maxHeight: isVisible && mediaDrawerRelationshipGate ? `calc(100dvh - 11.6625rem)` : "100%",
        display: "flex",
        flexDirection: "column",
        transition: "width 400ms"
      }}
      id='flexible-container'
    >
      {children}
    </Box>
  );
}

interface CalendarRendererProps {
  activeEndpoint: ReturnType<typeof useSelectionSelector>["selectedEndpoint"];
  isOpen: boolean;
  onToggle: () => void;
}

const CalendarRenderer = ({ activeEndpoint, isOpen, onToggle }: CalendarRendererProps) => {
  return (
    <>
      {/* Slide-down calendar panel */}
      <Box
        sx={{
          position: "fixed",
          top: "66px",
          left: 0,
          width: "100%",
          zIndex: 1400,
          overflowX: "hidden",
          overflowY: isOpen ? "auto" : "hidden",
          maxHeight: isOpen ? CALENDAR_HEIGHT : "0px",
          transition: "max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: isOpen ? 4 : 0
        }}
      >
        <CalendarView activeEndpoint={activeEndpoint} currentTab={1} height={CALENDAR_HEIGHT} />
      </Box>

      {/* Floating toggle button — anchored just below the navbar */}
      <Tooltip title={isOpen ? "Hide Timeline" : "Show Timeline"} placement='right' arrow>
        <IconButton
          onClick={onToggle}
          size='small'
          sx={{
            position: "fixed",
            // Sit right at the bottom edge of the (collapsed or expanded) panel
            top: isOpen ? `calc(66px + ${CALENDAR_HEIGHT})` : "calc(66px + 4px)",
            transition: "top 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1401,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            boxShadow: 3,
            width: 72,
            height: 22,
            borderRadius: "0 0 12px 12px",
            "&:hover": {
              bgcolor: "primary.dark",
              boxShadow: 6
            },
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CalendarMonthIcon sx={{ fontSize: 13 }} />
          <KeyboardArrowUpIcon
            sx={{
              fontSize: 13,
              transition: "transform 0.3s ease",
              transform: isOpen ? "rotate(0deg)" : "rotate(180deg)"
            }}
          />
        </IconButton>
      </Tooltip>
    </>
  );
};

const MapContent = ({
  isHomePage,
  showTimeline,
  calendarHeight
}: {
  isHomePage: boolean;
  showTimeline: boolean;
  calendarHeight: number;
}) => {
  const { setCommonUIState, triggerZoomToHome } = useUIActions();
  useSyncMapHighlights();

  // Ensure Sidebar is always Closed in Home Page and Open in Map pages
  useEffect(() => {
    if (isHomePage) {
      setCommonUIState({
        showSidebar: false
      });
      triggerZoomToHome();
    } else {
      setCommonUIState({
        showSidebar: true
      });
    }
  }, [isHomePage, setCommonUIState, triggerZoomToHome]);

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: showTimeline ? `${calendarHeight}px` : 0,
          left: 0,
          height: showTimeline ? `calc(100% - ${calendarHeight}px)` : "100%",
          width: "100%",
          display: "flex",
          zIndex: 2,
          pointerEvents: "none",
          transition: "top 0.35s cubic-bezier(0.4, 0, 0.2, 1), height 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      >
        <Box sx={{ zIndex: 2 }}>
          <Sidebar calendarOffset={showTimeline ? calendarHeight : 0} />
        </Box>
        <FlexibleContainer>
          {/* Render Non-geo, media modals*/}
          <Modals />
        </FlexibleContainer>
      </Box>

      <Grid
        item
        xs={12}
        sx={{
          position: "relative",
          // Start the map below the timeline when it is open (like the media drawer),
          // instead of letting the timeline overlap the map. Returns to full height when closed.
          marginTop: showTimeline ? `${calendarHeight}px` : 0,
          height: showTimeline ? `calc(100% - ${calendarHeight}px)` : "100%",
          transition: "margin-top 0.35s cubic-bezier(0.4, 0, 0.2, 1), height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1
        }}
      >
        <ErrorBoundary>
          <MapComponent isSidePanelOpen={true} hideControls={isHomePage} />
        </ErrorBoundary>
      </Grid>
      <MediaDrawer timelineOffsetPx={showTimeline ? calendarHeight : 0} />
      {/* Top-level Layer B host — see MediaDetailOverlayHost docs above. */}
      <MediaDetailOverlayHost />
    </>
  );
};

const Map = () => {
  const { pathname } = useLocation();
  const { selectedEndpoint, selectedEntityId } = useSelectionSelector();
  const { isMediaDetailOpen } = useMediaSelector();
  const { isFullViewerOpen } = usePopupSelector();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const isHomePage = pathname === "/";
  const isFullscreenMediaActive = !isHomePage && (isMediaDetailOpen || isFullViewerOpen);

  const showTimeline = useMemo(
    () => !isHomePage && !isFullscreenMediaActive && isCalendarOpen && selectedEndpoint?.dateConfig,
    [isCalendarOpen, selectedEndpoint?.dateConfig, isHomePage, isFullscreenMediaActive]
  );

  useEffect(() => {
    if (isFullscreenMediaActive && isCalendarOpen) {
      setIsCalendarOpen(false);
    }
  }, [isFullscreenMediaActive, isCalendarOpen]);

  return (
    <div
      style={{
        height: `calc(100vh - ${NAVBAR_HEIGHT})`,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {isHomePage && <StartupModal />}

      {selectedEndpoint?.dateConfig && !isHomePage && !isFullscreenMediaActive && (
        <CalendarRenderer activeEndpoint={selectedEndpoint} isOpen={isCalendarOpen} onToggle={() => setIsCalendarOpen((prev) => !prev)} />
      )}

      <MapContent
        isHomePage={isHomePage}
        showTimeline={!!showTimeline}
        calendarHeight={CALENDAR_HEIGHT_PX}
        key={isHomePage ? "home" : "map"}
      />
    </div>
  );
};

export default Map;
